import React, { useEffect, useState } from "react";
import { useDebounce } from "primereact/hooks";
import { Card, Col, Row } from "react-bootstrap";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import ThemeCustomDropdown from "../../../components/common/CustomDropdown";
import RenderAction from "../../../components/common/dataGrid/ActionCell";
import DataGridCommon from "../../../components/common/dataGrid/DataGridCommon";
import TableDataTooltip from "../../../components/common/dataGrid/TableDataTooltip";
import GenericModal from "../../../components/common/GenericModal";
import ThemeInputSearch from "../../../components/common/InputSearch";
import Loader from "../../../components/common/loader";
import SvgIcons from "../../../components/common/SvgIcons";
import { BreadCrumb } from "primereact/breadcrumb";
import { FiSearch } from "react-icons/fi";

import { ReactComponent as HomeIcon } from "../../../assets/images/home.svg";
import {
  generateQR,
  getAdminUserDetails,
} from "../../../services/admin.users.service";
import { useDataStore } from "../../../redux/data-store";
const UsersList = () => {
  const {
    loading,
    totalRecords,
    currentPage,
    rows,
    sortOrder,
    sortField,
    selectedRow,
    deleteShow,
    deleteId,
    setSearch,
    setCurrentPage,
    setRows,
    setSortOrder,
    setSortField,
    setSelectedRow,
    setDeleteShow,
    setDeleteId,
    role,
    roles,
    setRole,
    fetchRoles,
    users,
    stores,
    departments,
    countries,
    provinces,
    cities,
    country,
    province,
    city,
    store,
    department,
    setCountry,
    setProvince,
    setProvinces,
    setCity,
    setCities,
    setStore,
    setStores,
    setDepartment,
    setDepartments,
    fetchCountries,
    fetchProvinces,
    fetchCities,
    fetchStores,
    fetchDepartments,
    fetchUsers,
    userRecordDelete,
    reset,
  } = useDataStore();
  const [searchValue, debouncedSearchValue, setSearchValue] =
    useDebounce<string>("", 400);

  useEffect(() => {
    setSearch(debouncedSearchValue);
  }, [debouncedSearchValue, setSearch]);
  const [qrShow, setQrShow] = useState(false);
  const [qrConfirmShow, setQrConfirmShow] = useState(false);
  const [selectedQrData, setSelectedQrData] = useState<any>(null);
  const [qrGenerating, setQrGenerating] = useState(false);

  useEffect(() => {
    reset();
  }, [reset]);

  //Action Template
  const actionCellTemplate = (rowData: any) => (
    <RenderAction
      controlId="users"
      rowData={rowData}
      customButtons={[
        {
          name: "edit",
          enabled: true,
          type: "link",
          title: "Edit",
          class: "edit",
          icon: SvgIcons.editIcon,
        },
        {
          name: "delete",
          enabled: true,
          type: "button",
          title: "Delete",
          class: "delete",
          icon: SvgIcons.deleteIcon,
          handler: () => handleDeleteClick(rowData),
        },
        // {
        // 	name: 'verification',
        // 	enabled: true,
        // 	type: 'button',
        // 	title: 'Send Verification',
        // 	class: 'verification',
        // 	icon: SvgIcons.keyIcon,
        // }
      ]}
    />
  );

  //Handle Delete
  const handleDeleteClick = (row: any) => {
    setSelectedRow(row);
    setDeleteId(row.id);
    setDeleteShow(true);
  };

  //Handle QR View
  const handleQrClick = (row: any) => {
    setSelectedQrData(row);
    setQrShow(true);
  };

  const qrConfirmPopup = async () => {
    setQrConfirmShow(true);
  };

  //Handle QR Generation
  const handleGenerateQR = async () => {
    if (!selectedQrData?.id) return;

    setQrGenerating(true);
    try {
      await generateQR(selectedQrData.id);
      toast.success("QR Code generated successfully!");
      // Refresh the users list to get updated QR data in the table
      fetchUsers();
      // Refresh data for the open modal without closing it
      try {
        const details = await getAdminUserDetails(String(selectedQrData.id));
        if (details?.statusCode === 200 && details?.data) {
          setSelectedQrData(details.data);
        }
      } catch {
        console.log("Error getAdminUserDetails");
      }
    } catch (error) {
      toast.error("Failed to generate QR code");
    } finally {
      setQrGenerating(false);
      setQrConfirmShow(false);
    }
  };

  const onSort = (sortOrder: string, field: string) => {
    setSortField(field);
    setSortOrder(sortOrder);
  };

  const handlePageChange = (newPage: number, newRowsPerPage: number) => {
    setRows(newRowsPerPage);
    setCurrentPage(newPage);
  };

  //Handle Search
  const handleSearchTextChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setSearchValue(event.target.value);
  };

  useEffect(() => {
    fetchCountries();
    fetchStores();
    fetchRoles();
  }, [fetchCountries, fetchStores, fetchRoles]);

  useEffect(() => {
    fetchUsers();
  }, [
    currentPage,
    rows,
    debouncedSearchValue,
    sortField,
    sortOrder,
    country,
    province,
    city,
    store,
    department,
    role,
    fetchUsers,
  ]);

  const items = [{ label: "Users" }, { label: "Users List" }];
  const home = {
    icon: <HomeIcon style={{ width: "1rem", height: "1rem" }} />,
    url: "/dashboard",
  };

  // Table Header
  const columns = [
    {
      field: "name",
      header: "Name",
      sortable: true,
      body: (rowData: any) => (
        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          {rowData?.profile_photo && (
            <img
              src={`/${rowData.profile_photo}`}
              alt={rowData.name}
              style={{ width: "32px", height: "32px", borderRadius: "50%" }}
            />
          )}
          <TableDataTooltip tableData={rowData?.name} />
        </div>
      ),
    },

    {
      field: "role",
      header: "Role",
      sortable: false,
      body: (rowData: any) => (
        <TableDataTooltip tableData={rowData?.role?.title || "-"} />
      ),
    },
    {
      field: "email",
      header: "Email",
      sortable: true,
      body: (rowData: any) => (
        <TableDataTooltip
          wrapperClassname="text-decoration-underline"
          linkUrl={`mailto:${rowData?.email}`}
          tableData={rowData?.email}
        />
      ),
    },

    {
      field: "department",
      header: "Department",
      sortable: true,
      body: (rowData: any) => (
        <TableDataTooltip tableData={rowData?.department?.name || "-"} />
      ),
    },

    {
      field: "store",
      header: "Store",
      sortable: true,
      body: (rowData: any) => (
        <TableDataTooltip tableData={rowData?.store?.name || "-"} />
      ),
    },

    {
      field: "city",
      header: "City",
      sortable: true,
      body: (rowData: any) => (
        <TableDataTooltip tableData={rowData?.city?.name || "-"} />
      ),
    },

    {
      field: "province",
      header: "Province",
      sortable: true,
      body: (rowData: any) => (
        <TableDataTooltip tableData={rowData?.city?.province?.name || "-"} />
      ),
    },

    {
      field: "country",
      header: "Country",
      sortable: true,
      body: (rowData: any) => (
        <TableDataTooltip
          tableData={rowData?.city?.province?.country?.name || "-"}
        />
      ),
    },

    {
      field: "last_login",
      header: "Last Login",
      sortable: true,
      body: (rowData: any) => {
        if (!rowData?.last_login) return <TableDataTooltip tableData="-" />;
        const date = new Date(rowData?.last_login);
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");
        const year = date.getFullYear();
        let hours = date.getHours();
        const minutes = String(date.getMinutes()).padStart(2, "0");
        const ampm = hours >= 12 ? "PM" : "AM";
        hours = hours % 12;
        hours = hours ? hours : 12;
        const formattedHours = String(hours).padStart(2, "0");
        const formattedDate = `${month}/${day}/${year} ${formattedHours}:${minutes} ${ampm}`;
        return <TableDataTooltip tableData={formattedDate} />;
      },
    },

    {
      field: "qr_code",
      header: "QR Code",
      sortable: false,
      body: (rowData: any) => (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "6px",
            cursor: "pointer",
            padding: "6px 10px",
            borderRadius: "6px",
            border: "1px solid transparent",
            transition: "all 0.2s ease",
          }}
          className="qr-code-cell"
          onClick={() => handleQrClick(rowData)}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = "#e3f2fd";
            e.currentTarget.style.borderColor = "#2196f3";
            e.currentTarget.style.transform = "translateY(-1px)";
            e.currentTarget.style.boxShadow =
              "0 2px 8px rgba(33, 150, 243, 0.15)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = "transparent";
            e.currentTarget.style.borderColor = "transparent";
            e.currentTarget.style.transform = "translateY(0)";
            e.currentTarget.style.boxShadow = "none";
          }}
        >
          <div style={{ color: "#2196f3" }}>{SvgIcons.QRCodeIcon}</div>
          <span
            style={{ color: "#2196f3" }}
            className="text-decoration-underline fw-medium"
          >
            View
          </span>
        </div>
      ),
    },

    {
      field: "action",
      header: "Action",
      headerClassName: "theme-action-cell",
      width: "120px",
      body: actionCellTemplate,
    },
  ];

  // data is loaded from API
  return (
    <React.Fragment>
      <Card className="overflow-auto border-0 rounded-bottom-0 h-100 page-card-content px-3 pt-3 position-relative">
        <Loader
          isLoading={loading}
          fullscreen={false}
          reason="users-list"
        />
        <Card.Header className="fs-5 fw-semibold  d-flex flex-wrap gap-2 align-items-center bg-body-tertiary border-0 px-0 pt-0 pb-3">
          <div className="w-100">
            <BreadCrumb model={items} home={home} />
          </div>
          <div className="w-100 d-flex flex-wrap align-items-center justify-content-between ">
            <h5 className="mb-0 me-auto fw-bold text-uppercase">Users</h5>
            <Link to="add" className="btn btn-primary">
              Add New
            </Link>
          </div>
        </Card.Header>
        <Card.Body className="d-flex flex-column   bg-white p-3 rounded-3">
          <div className="table-header p-3">
            <Row className="g-2">
              <Col sm className="d-flex position-relative">
                <ThemeInputSearch
                  wrapperClassname="custom-width-240 mw-100 flex-grow-1 flex-md-grow-0 search-input"
                  controlId="search"
                  value={searchValue}
                  placeholder="Search by Name or Email"
                  handleChange={handleSearchTextChange}
                />
                <FiSearch className="position-absolute search-icon" />
              </Col>
              <Col sm="auto">
                <Row className="g-2 justify-content-end">
                  <Col sm md="auto" className="d-flex">
                    <ThemeCustomDropdown
                      wrapperClassname="custom-width-140 mw-100 flex-grow-1 flex-md-grow-0"
                      inputClassName="p-inputtext-sm"
                      controlId="country"
                      placeHolder="All Countries"
                      data={countries}
                      value={country}
                      handleChange={(e: any) => {
                        setCountry(e.value);
                        if (e.value !== -1) {
                          fetchProvinces(e.value);
                        } else {
                          setProvinces([]);
                        }
                        setCities([]);
                      }}
                    />
                  </Col>

                  <Col sm md="auto" className="d-flex">
                    <ThemeCustomDropdown
                      wrapperClassname="custom-width-140 mw-100 flex-grow-1 flex-md-grow-0"
                      inputClassName="p-inputtext-sm"
                      controlId="province"
                      placeHolder="All Provinces"
                      data={provinces}
                      value={province}
                      handleChange={(e: any) => {
                        setProvince(e.value);
                        if (e.value !== -1) {
                          fetchCities(e.value);
                        } else {
                          setCities([]);
                        }
                      }}
                      emptyMessage="Select a Country first"
                    />
                  </Col>

                  <Col sm md="auto" className="d-flex">
                    <ThemeCustomDropdown
                      wrapperClassname="custom-width-140 mw-100 flex-grow-1 flex-md-grow-0"
                      inputClassName="p-inputtext-sm"
                      controlId="city"
                      placeHolder="All Cities"
                      data={cities}
                      value={city}
                      handleChange={(e: any) => {
                        setCity(e.value);
                        if (e.value !== -1) {
                          fetchStores(e.value);
                        } else {
                          setStores([]);
                        }
                        setDepartments([]);
                      }}
                      emptyMessage="Select a Province first"
                    />
                  </Col>

                  <Col sm md="auto" className="d-flex">
                    <ThemeCustomDropdown
                      wrapperClassname="custom-width-140 mw-100 flex-grow-1 flex-md-grow-0"
                      inputClassName="p-inputtext-sm"
                      controlId="store"
                      placeHolder="All Stores"
                      data={stores}
                      value={store}
                      handleChange={(e: any) => {
                        setStore(e.value);
                        if (e.value !== -1) {
                          fetchDepartments(e.value);
                        } else {
                          setDepartments([]);
                        }
                      }}
                      emptyMessage="Select a City first"
                    />
                  </Col>

                  <Col sm md="auto" className="d-flex">
                    <ThemeCustomDropdown
                      wrapperClassname="custom-width-140 mw-100 flex-grow-1 flex-md-grow-0"
                      inputClassName="p-inputtext-sm"
                      controlId="department"
                      placeHolder="All Departments"
                      data={departments}
                      value={department}
                      handleChange={(e: any) => {
                        setDepartment(e.value);
                      }}
                      emptyMessage="Select a Store first"
                    />
                  </Col>
                  <Col sm md="auto" className="d-flex">
                    <ThemeCustomDropdown
                      wrapperClassname="custom-width-140 mw-100 flex-grow-1 flex-md-grow-0"
                      inputClassName="p-inputtext-sm"
                      controlId="roleFilter"
                      placeHolder="All Roles"
                      data={roles}
                      value={role}
                      handleChange={(e: any) => {
                        setRole(e.value);
                      }}
                    />
                  </Col>
                </Row>
              </Col>
            </Row>
          </div>
          <div className="flex-grow-1 mh-100 overflow-auto">
            <DataGridCommon
              columns={columns}
              data={users}
              currentPage={currentPage}
              onPageChange={handlePageChange}
              onSort={onSort}
              tableRecords={totalRecords}
              rowPerPage={rows}
              sortingOrder={sortOrder}
              sortField={sortField}
              emptyMessage={
                <div className="text-center w-full">No matching User found</div>
              }
            />
          </div>
        </Card.Body>
      </Card>

      {/* Delete Modal */}
      <GenericModal
        show={deleteShow}
        handleClose={() => setDeleteShow(false)}
        genericModalHeaderTitle={`Delete User`}
        genericModalBodyContent={
          <div className="text-center">
            <div className="mb-4 mt-3">
              <svg
                width="50"
                height="44"
                viewBox="0 0 50 44"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M49.5003 38.475L28.2112 1.84213C26.7834 -0.614045 23.2166 -0.614045 21.7888 1.84213L0.49967 38.475C-0.922571 40.9312 0.860766 44 3.71081 44H46.2892C49.1392 44 50.9226 40.9312 49.5003 38.475ZM22.9889 12.7873H27.0111C27.85 12.7873 28.5112 13.4993 28.4389 14.3272L27.5389 24.9909C27.4778 25.7305 26.8556 26.299 26.1111 26.299H23.8889C23.1444 26.299 22.5222 25.7305 22.4611 24.9909L21.5611 14.3272C21.4888 13.4993 22.15 12.7873 22.9889 12.7873ZM25 38.039C23.0889 38.039 21.5389 36.499 21.5389 34.6003C21.5389 32.7016 23.0889 31.1617 25 31.1617C26.9111 31.1617 28.4611 32.7016 28.4611 34.6003C28.4611 36.499 26.9111 38.039 25 38.039Z"
                  fill="#E24C4C"
                />
              </svg>
            </div>
            <h2 className="fs-20 fw-bold text-gray-900">Are you sure</h2>
            <p className="mb-0 fs-16 mt-2 text-gray-900">
              You want to delete <strong>{selectedRow?.name}</strong>
              <div>It will restrict the user access on the App.</div>
            </p>
          </div>
        }
        handleAction={() => userRecordDelete(deleteId)}
        buttonName="Delete"
      />

      {/* QR Code Modal */}
      <GenericModal
        show={qrShow}
        handleClose={() => setQrShow(false)}
        genericModalHeaderTitle={`QR Code - ${selectedQrData?.name || "User"}`}
        genericModalBodyContent={
          <div className="text-center">
            <div className="mt-3">
              {selectedQrData?.qr_path ? (
                <div>
                  <div className="position-relative d-inline-block">
                    <img
                      src={`/${selectedQrData.qr_path}`}
                      alt={`QR Code for ${selectedQrData.name}`}
                      style={{
                        minWidth: "300px",
                        minHeight: "300px",
                        maxWidth: "300px",
                        maxHeight: "300px",
                        width: "100%",
                        height: "auto",
                      }}
                      className="border rounded shadow-sm"
                    />
                    <div
                      className="position-absolute top-0 m-2"
                      style={{ right: "-50px" }}
                    >
                      <button
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => {
                          const link = document.createElement("a");
                          link.href = `/${selectedQrData.qr_path}`;
                          link.download = `qr-code-${selectedQrData.name?.toLowerCase().replace(/\s+/g, "-")}.png`;
                          document.body.appendChild(link);
                          link.click();
                          document.body.removeChild(link);
                        }}
                        title="Download QR Code"
                      >
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M21 15V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V15"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M7 10L12 15L17 10"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M12 15V3"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </button>
                    </div>
                  </div>
                  <div className="mt-1 rounded">
                    <div className="row text-center">
                      <div className="col-12 mb-2">
                        <span className="fw-semibold text-dark">
                          QR Code for:
                        </span>
                        <span className="ms-1 text-muted">
                          {`${selectedQrData?.name || "User"}(${selectedQrData?.email || ""})`}
                        </span>
                      </div>
                      {/* <div className="col-12 mb-2">
                        <span className="fw-semibold text-dark">Email:</span>
                        <span className="ms-2 text-muted">
                          {selectedQrData?.email}
                        </span>
                      </div> */}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="py-5">
                  <div className="mb-3">
                    {React.cloneElement(SvgIcons.QRCodeIcon as any, {
                      width: 64,
                      height: 64,
                    })}
                  </div>
                  <h5 className="text-muted">No QR Code Available</h5>
                  <p className="text-muted mb-0 mb-3">
                    This user doesn't have a QR code generated yet.
                  </p>
                </div>
              )}
            </div>
          </div>
        }
        handleAction={qrConfirmPopup}
        buttonName={qrGenerating ? "Generating..." : "Generate QR"}
        actionVariant="primary"
        actionDisabled={qrGenerating}
        genericModalSize="md"
      />

      {/* QR Code Modal */}
      <GenericModal
        show={qrConfirmShow}
        handleClose={() => setQrConfirmShow(false)}
        genericModalHeaderTitle={`Are you sure?`}
        genericModalBodyContent={
          <div className="text-center">
            <div className="mt-3">
              Are you sure You want to generate a new QR code?
              <br /> <strong>Note:</strong> This will overwrite the existing QR
              code.
            </div>
          </div>
        }
        handleAction={handleGenerateQR}
        buttonName={qrGenerating ? "Generating..." : "Yes"}
        actionVariant="primary"
        actionDisabled={qrGenerating}
        genericModalSize="md"
      />
    </React.Fragment>
  );
};

export default UsersList;
